<template>
  <div ref="hero" class="relative z-10 flex max-w-full overflow-hidden feature-hero-wrapper fix-it bg-forest-green" data-test-id="component-feature-hero">
    <div class="relative w-full h-full feature-hero-inner main-grid md:pt-0 lg:container">
      <BlocksHeroBreadcrumbs :breadcrumbs="breadcrumbs" class="absolute breadcrumbs top-4 left-4 md:top-8 md:left-8 lg:left-4" theme="yellow" :class="[(animatedHero) ? 'animate': '']" />
      <div
        class="relative z-10 flex text-center h-86 title lg:h-auto lg:items-end lg:text-left ml-half-space lg:ml-0 justify-items-center md:align-top md:justify-items-start mt-0 lg:mt-0"
        :class="{
          'animate': animatedHero,
          'mb-0': noImage,
          'mb-0 lg:mb-4': !noImage,
        }"
      >
        <h1 class="m-auto uppercase lg:m-0 font-druk text-display-14 md:text-display-25 leading-12 md:leading-20 -tracking-01-em md:tracking-02-em text-bright-yellow">
          <span v-for="(line, index) in breakedTitle" :key="index" class="block overflow-hidden last:mr-0">
            <span class="inline-block text">{{ line }}</span>
          </span>
        </h1>
      </div>

      <div
        class="relative z-0 main-animation"
        :class="{
          'animate': animatedHero,
          ' lg:block': noImage
        }"
      >
        <div class="relative items-stretch h-full -mx-4 text-white mini-checkout-services bg-dark lg:mx-0 lg:flex">
          <div class="relative layout-arrow h-11 lg:hidden">
            <svg preserveAspectRatio="none" viewBox="0 0 320 45" class="absolute w-full h-full layout-arrow-top text-forest-green">
              <polygon points="0,0 320,0 320,45 160,45 0,45" fill="currentColor">
                <animate
                  begin="indefinite"
                  fill="freeze"
                  attributeName="points"
                  dur="500ms"
                  to="0,0 320,0 320,0 160,45 0,0"
                  class="arrow-animation"
                />
              </polygon>
            </svg>
          </div>
          <div class="relative hidden w-20 min-h-full bg-dark lg:block">
            <svg preserveAspectRatio="none" viewBox="0 0 45 320" class="absolute inset-0 z-10 w-full h-full lg-arrow text-forest-green">
              <polygon points="0,0 45,160 0,320 0,0" fill="currentColor" />
            </svg>
          </div>
          <img
            v-if="img"
            draggable="false"
            class="absolute top-0 left-0 z-0 object-cover object-center w-full h-full"
            :src="img"
            alt="Hero image"
          >
        </div>
      </div>

      <div class="flex justify-center pt-8 mb-12 hero-body container-to-md lg:justify-start md:mb-20 lg:mb-auto md:pt-16 lg:pt-4 text-bright-yellow" :class="[(animatedHero) ? 'animate': '']">
        <div class="content">
          <!-- @slot Content for feature hero -->
          <slot :variant="textVariant" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { optimize } from '@/utils/image-optimization'
import { isDarkOrLightByElement } from '@/utils/contrast-calculator'

export default {
  props: {
    title: {
      type: String,
      required: true
    },
    img: {
      type: String,
      default: () => ''
    },
    isPhone: {
      type: Boolean,
      default: false
    },
    breadcrumbs: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  data () {
    return {
      animatedHero: false,
      textVariant: 'dark'
    }
  },
  computed: {
    frontImage () {
      return optimize('https://majority.imgix.net/web-assets/chore/iphone-front.png', false, {
        w: 420
      })
    },
    backImage () {
      return optimize('https://majority.imgix.net/web-assets/chore/iphone-back.png', false, {
        w: 420,
        duotone: '000000,000000',
        'duotone-alpha': 100
      })
    },
    breakedTitle () {
      const addSpaceAtTheEndOfWord = (word, wordIndex, senteceLength) => {
        if (wordIndex !== senteceLength - 1) { return word + ' ' } else { return word }
      }
      return this.title.split(' ').map((line, index) => addSpaceAtTheEndOfWord(line, index, this.title.split(' ').length))
    },
    hasBreadcrumbs () {
      return this.breadcrumbs.length > 0
    },
    noImage () {
      return !this.img
    }
  },
  mounted () {
    window.addEventListener('resize', this.updateTextVariant)
    this.updateTextVariant()

    this.animatedHero = true
  },
  unmounted () {
    window.removeEventListener('resize', this.updateTextVariant)
  },
  methods: {
    updateTextVariant () {
      this.textVariant = isDarkOrLightByElement(this.$refs.hero.querySelector('.hero-body'))
    }
  }
}
</script>

<style lang="postcss" scoped>
.feature-hero-wrapper {
  margin-top: var(--navbar-height);
  -webkit-transform: translateZ(0);
  backface-visibility: hidden;

  @screen lg {
    min-height: 640px !important;
    height: 640px;
  }
}

.feature-hero-inner {
  @screen lg {
    &:after {
      z-index: 0;
      position: absolute;
      display: block;
      width: 50vw;
      height: 100%;
      top: 0; right: 40px;
      transform: translate3d(100%, 0, 0.1px);
      background: #d29fab;
      content: '';
    }
  }
}

.breadcrumbs {
  opacity: 0;
  transition: opacity 0.8s 0s cubic-bezier(0.76, 0, 0.24, 1);

  @screen lg {
    transition: opacity 0.8s cubic-bezier(0.76, 0, 0.24, 1);
  }

  &.animate {
    opacity: 1;
  }
}

.main-grid {
  display: grid;
  grid-template-areas:
    "title"
    "visual"
    "body";

  @screen lg {
    grid-template-columns: 50% 50%;
    grid-template-rows: auto;
    grid-template-areas:
      "title visual"
      "body visual";
  }
}

.title {
  grid-area: title;

  .text {
    transform: translateY(100%);
  }

  &.animate {
    .text {
      animation: slideIn 0.8s 0.4s cubic-bezier(0.76, 0, 0.24, 1) forwards;
    }
  }
}

.hero-body {
  grid-area: body;
  position: relative;

  @screen lg {
    .text {
      transform: translateY(100%);
    }
    .content {
      opacity: 0;
      transition: opacity 0.8s cubic-bezier(0.76, 0, 0.24, 1);
    }
  }

  &.animate {
    @screen lg {
      .text {
        animation: slideIn 0.8s 0s cubic-bezier(0.76, 0, 0.24, 1) forwards;
      }

      .content {
        opacity: 1;
      }
    }
  }
}

.main-animation {
  grid-column: 1 / 2;
  grid-row: 1 / 3;

  -webkit-transform: translateZ(0);
  backface-visibility: hidden;
  z-index: 10;

  .phone-wrapper {
    transform: translateY(30px);
  }

  .phone {
    opacity: 0;
    max-width: 42vw;

    &.no-phone {
      max-width: 65vw;
    }
  }

  @screen md {
    .phone {
      max-width: 39vw;
      &.no-phone {
        max-width: 55vw;
      }
    }
  }

  @screen lg {
    min-height: auto;
    transition: none;
    grid-column: unset;
    grid-area: visual;
    .phone-wrapper {
      height: 100%;
      transform: translateY(0);
    }

    .phone {
      opacity: 0;
      max-width: 265px;
      transition: transform 0.8s cubic-bezier(0.76, 0, 0.24, 1);
      transform: translateY(-10%);

      &.no-phone {
        max-width: 350px;
      }
    }
  }

  &.animate {
    .phone-wrapper {
      animation: phoneSlideUp 0.8s 0.4s cubic-bezier(0.76, 0, 0.24, 1) forwards;
    }

    .phone {
      animation: show 0.8s 0.4s cubic-bezier(0.76, 0, 0.24, 1) forwards;
    }

    @screen lg {
      .phone-wrapper {
        animation: none;
      }

      .phone {
        animation: show 0.4s 0s cubic-bezier(0.76, 0, 0.24, 1) forwards;
        transform: translateY(0);
      }
    }
  }
}

@keyframes hide {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    width: 0;
  }
}

@keyframes show {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes phoneSlideUp {
  0% {
    transform: translateY(30px);
  }
  100% {
    transform: translateY(0);
  }
}

@keyframes slideIn {
  0% {
    transform: translateY(100%);
  }
  100% {
    transform: translateY(0);
  }
}

@keyframes growMaxHeight {
  0% {
    max-height: 0;
  }
  100% {
    max-height: 150vh;
  }
}

@keyframes shrinkMainAnimation {
  0% {
    min-height: calc(100vh - var(--navbar-height));
  }
  100% {
    min-height: 30vh;
  }
}
</style>
